import { isNull, isUndefined } from "./TypeUtil";

/**
 * Gets value(s) for key from url search string of window. 
 * @param {string} key Key to read from search string. It will be applied case-insensitive.
 * @param {Window} win Window object from which to read the `location.search`. Note that in cross-origin frames, it is not allowed to read from `window.top`.  
 * @param {boolean} caseSensitive Whether to apply the keys case-sensitive or not. Values will be unchanged.
 * @param {boolean} all Whether to return all occurences of `key`. This wil return an Array, even if there is only 1 value.
 * @returns {string|Array.<string>|null} - Will return null if key not found. Will return an array if `all` is true. Else will return a string.
 */
 export function getURLParameter(key, win = window, caseSensitive = false, all = false) {
    try {
        let params = new URLSearchParams(win.location.search);
        if (!caseSensitive) {
            key = key.toLowerCase();
            const paramsLowerCase = new URLSearchParams();
            for (const [name, value] of params) {
                paramsLowerCase.set(name.toLowerCase(), value);
            }
            params = paramsLowerCase;
        }
        return all ? params.getAll(key) : params.get(key);
    } catch (e) {
        //An error may occur when retrieving `location.search` from `window.top` in a cross-origin situation.
        console.error(`Error reading URL paramater: ${e}`);
        // return as if the key is not found
        return null;
    }
};


/**
 * Replaces values in objects by values specified in url query string ('?key=value&key2=value2')  
 * This method is meant for debug purposes. There is no validation on the values in the query string.  
 * Keys can be abbreviated (e.g. 'useHLS' -> 'us' or 'uh' or any other combination of letters). First key in the object that matches the abbreviation will be used.  
 * Keys can point to nested keys in the object using dot notation. E.g. 'settings.useHLS=1' or abbreviated 'stt.uh=1'.
 * @param {String} queryString - the query string of an url. Typically, this is the value of window.location.search or window.top.location.search
 * @param {Object} object - Object in which to replace values by values from queryString
 */
export function applySearchParametersToObject(queryString, object) {
    const params = new URLSearchParams(queryString);
    params.forEach(function(value, key) {
        const res = findKey(object, key);
        if (res) {
            const curr = res.object[res.key];
            const parsed = parseQueryValue(curr, value);
            if (parsed != undefined) {
                res.object[res.key] = parsed;
                console.log(`Overwrite object key '${res.path}', old: ${curr}, new: ${res.object[res.key]}, raw: "${value}"`);
            }
        }
    });
}

//Not exported:

function findKey(obj, abbr) {
    const parts = abbr.split("."), np = parts.length;
    const keyParts = [];
    let key;
    for (let i = 0; i < np; i++) {
        const part = parts[i];
        let regex = new RegExp("^" + part.split("").join(".*?"), "i");
        for (const s in obj) {
            if (regex.test(s)) {
                if (i < np - 1) obj = obj[s];
                else key = s;
                keyParts.push(s);
                break;
            }
        }
    }
    if (key != null) {
        return { object: obj, key: key, path: keyParts.join(".") };
    }
}

function parseQueryValue(currentValue, queryValue) {
    //NB: No validation on type of queryValue
    switch (typeof currentValue) {
        case "number":
            queryValue = Number(queryValue);
            break;
        case "boolean":
            queryValue = queryValue == "false" || queryValue == "0" ? false : Boolean(queryValue);
            break;
        case "string":
            //If currentValue is a hex value, interpret the query string as such
            if (currentValue.charAt(0) == "#") queryValue = "#" + queryValue;
            break;
        case "object":
            if (Array.isArray(currentValue)) {
                queryValue = queryValue.split(",").map((elem, idx) => { return parseQueryValue(currentValue[idx], elem); });
                if (queryValue.indexOf(undefined) > -1) {
                    return;
                }
            } else {
                console.warn("Object type not supported. Directly access nested object values using '.' notation in query string.");
                return;
            }
            break;
    }
    return queryValue;
}